/**
 * Semantic tokens — what UI consumes.
 *
 * Dark mode (KAN-304, step 1): every COLOUR value here is a `var(--cvx-<group>-<key>)` string.
 * The values behind the variables live in `themePalettes.js` (LIGHT + GRAPHITE) and are injected
 * as CSS by `applyThemeVars.js`; the key names are unchanged, so no consumer's import moved.
 *
 * A consumer that needs a REAL colour (colour math such as hex + alpha, `parseInt(hex)`, a PDF, an
 * SVG data URI) must not parse these strings — use `alphaOf` / `withHexAlpha`
 * (`src/utils/ui/colorAlpha.js`) for alpha mixing, or read `lightLiteral` below for a fixed Light
 * colour. Never the other way round: `lightLiteral` does NOT follow the theme.
 */

import { semanticLiteral } from './semanticLiteral';

/** Colour-valued groups of the literal tokens (everything except fontFamily + transition). */
export const COLOR_GROUPS = ['bg', 'fg', 'border', 'brand', 'status', 'role', 'roleSoft', 'roleBorder', 'elevation', 'nav', 'dataRamp', 'homeEdge', 'groupedList', 'legacy', 'ink', 'tint', 'edge', 'wash', 'chart', 'industry'];

/** The CSS custom property that carries `<group>.<key>`. */
export const themeVarName = (group, key) => `--cvx-${group}-${key}`;

const toVars = (group, values) => Object.fromEntries(
  Object.entries(values).map(([key, value]) => [key, value === 'none' ? value : `var(${themeVarName(group, key)})`]),
);

/** The LIGHT literal values in the same object shape as `semantic` — fixed, never theme-aware. */
export const lightLiteral = semanticLiteral;

export const bg = toVars('bg', semanticLiteral.bg);
export const fg = toVars('fg', semanticLiteral.fg);
export const border = toVars('border', semanticLiteral.border);
export const brand = toVars('brand', semanticLiteral.brand);
export const status = toVars('status', semanticLiteral.status);
export const role = toVars('role', semanticLiteral.role);
export const roleSoft = toVars('roleSoft', semanticLiteral.roleSoft);
export const roleBorder = toVars('roleBorder', semanticLiteral.roleBorder);
export const elevation = toVars('elevation', semanticLiteral.elevation);
export const nav = toVars('nav', semanticLiteral.nav);
export const dataRamp = toVars('dataRamp', semanticLiteral.dataRamp);
export const homeEdge = toVars('homeEdge', semanticLiteral.homeEdge);
export const groupedList = toVars('groupedList', semanticLiteral.groupedList);
export const legacy = toVars('legacy', semanticLiteral.legacy);
export const ink = toVars('ink', semanticLiteral.ink);
export const tint = toVars('tint', semanticLiteral.tint);
export const edge = toVars('edge', semanticLiteral.edge);
export const wash = toVars('wash', semanticLiteral.wash);
export const chart = toVars('chart', semanticLiteral.chart);
export const industry = toVars('industry', semanticLiteral.industry);
export const { fontFamily, transition } = semanticLiteral;

export const semantic = {
  fontFamily,
  bg,
  fg,
  border,
  brand,
  status,
  role,
  roleSoft,
  roleBorder,
  elevation,
  nav,
  dataRamp,
  homeEdge,
  groupedList,
  legacy,
  ink,
  tint,
  edge,
  wash,
  chart,
  industry,
  transition,
};
